import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { baseUrl } from "../../config";
import {
  Alert,
  Button,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import styled from "@emotion/styled";
import React from "react";
import { Helmet } from "react-helmet-async";
import { trackEmailVerified } from "../../analytics/signupTracking";
import * as Yup from "yup";
import { Formik } from "formik";

const Wrapper = styled(Paper)`
  padding: ${(props) => props.theme.spacing(6)};
  background-color: #f7f9fc;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: ${(props) => props.theme.spacing(10)};
  }
`;

const passwordSchema = Yup.object().shape({
  password: Yup.string()
    .min(8, "Salasanan täytyy olla vähintään 8 merkkiä")
    .required("Salasana on pakollinen"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Salasanat eivät täsmää")
    .required("Vahvista salasana"),
});

const Activate = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(5);
  // postal mode: after activation, user must set a new password
  const [postalResetToken, setPostalResetToken] = useState<string | null>(null);
  const [passwordSet, setPasswordSet] = useState<boolean>(false);
  const [passwordCountdown, setPasswordCountdown] = useState<number>(3);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("token");
    const mode = params.get("mode");

    if (token) {
      const url = mode
        ? `${baseUrl}/auth/activate?token=${token}&mode=${mode}`
        : `${baseUrl}/auth/activate?token=${token}`;

      axios
        .get(url)
        .then((response) => {
          trackEmailVerified();
          if (response.data?.mode === "postal" && response.data?.passwordResetToken) {
            setPostalResetToken(response.data.passwordResetToken);
          } else {
            setSuccess(true);
          }
        })
        .catch((err) => {
          console.error("Verification failed:", err);
          setError("Aktivointilinkki on vanhentunut tai virheellinen. Ota yhteyttä tiehoitokunnan hallintajäseneen.");
        });
    }
  }, [location.search]);

  // Email verification: countdown then redirect
  useEffect(() => {
    if (success && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (success && countdown === 0) {
      navigate("/login");
    }
  }, [success, countdown, navigate]);

  // Postal mode: after password is set, countdown then redirect to login
  useEffect(() => {
    if (passwordSet && passwordCountdown > 0) {
      const timer = setTimeout(
        () => setPasswordCountdown(passwordCountdown - 1),
        1000
      );
      return () => clearTimeout(timer);
    } else if (passwordSet && passwordCountdown === 0) {
      navigate("/login");
    }
  }, [passwordSet, passwordCountdown, navigate]);

  const handleSetPassword = async (
    values: { password: string; confirmPassword: string },
    { setSubmitting, setFieldError }: any
  ) => {
    try {
      await axios.post(`${baseUrl}/auth/set-new-password`, {
        token: postalResetToken,
        newPassword: values.password,
      });
      setPasswordSet(true);
    } catch (err: any) {
      setFieldError(
        "password",
        err.response?.data?.message || "Salasanan asettaminen epäonnistui."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Postal mode — password setup form
  if (postalResetToken && !passwordSet) {
    return (
      <React.Fragment>
        <Wrapper>
          <Helmet title="Aseta salasana" />
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            Tunnus aktivoitu
          </Typography>
          <Typography variant="body1" align="center" sx={{ mb: 3 }}>
            Aseta salasana tilillesi kirjautumista varten.
          </Typography>
          <Formik
            initialValues={{ password: "", confirmPassword: "" }}
            validationSchema={passwordSchema}
            onSubmit={handleSetPassword}
          >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
              <form noValidate onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  type="password"
                  name="password"
                  label="Uusi salasana"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touched.password && errors.password)}
                  helperText={touched.password && errors.password}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  type="password"
                  name="confirmPassword"
                  label="Vahvista salasana"
                  value={values.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                  helperText={touched.confirmPassword && errors.confirmPassword}
                  margin="normal"
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  sx={{ mt: 2 }}
                >
                  Aseta salasana
                </Button>
              </form>
            )}
          </Formik>
        </Wrapper>
      </React.Fragment>
    );
  }

  // Postal mode — password set successfully
  if (passwordSet) {
    return (
      <React.Fragment>
        <Wrapper>
          <Helmet title="Valmis" />
          <Alert severity="success" sx={{ mb: 3 }}>
            Salasana asetettu! Sinut ohjataan kirjautumissivulle{" "}
            {passwordCountdown} sekunnin kuluttua...
          </Alert>
        </Wrapper>
      </React.Fragment>
    );
  }

  // Standard email verification flow
  return (
    <React.Fragment>
      <Wrapper>
        <Helmet title="Activate" />
        <Typography
          my={10}
          component="h1"
          variant="h1"
          align="center"
          gutterBottom
        >
          Tiehoitokunta.fi
        </Typography>
        <Typography my={10} component="h2" variant="body1" align="center">
          {error
            ? error
            : success
            ? `Tunnus aktivoitu onnistuneesti! Sinut ohjataan kirjautumissivulle ${countdown} sekunnin kuluttua...`
            : "Aktivoidaan tunnusta..."}
        </Typography>
        {error && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Button href={"/"} variant="contained" color="primary">
              Etusivu
            </Button>
          </div>
        )}
      </Wrapper>
    </React.Fragment>
  );
};

export default Activate;
